import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import App from "./App";
import LoginPage from "./components/LoginPage";
import OAuthCallback from "./components/OAuthCallback";
import RegistrationPage from "./components/RegistrationPage";
import WorkflowBuilder from "./components/WorkflowBuilder";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import "./index.css";
import "./styles/components.css";
import "./styles/layout.css";
import "./styles/markdown.css";
import "./styles/theme.css";
import "./styles/LoginPage.css";
import reportWebVitals from './reportWebVitals';
import { HelmetProvider } from "react-helmet-async";

// --- CANONICALIZATION (must stay native — runs before React mounts) ---
if (
  process.env.NODE_ENV === 'production' &&
  window.location.hostname !== 'www.stackform.ai' &&
  window.location.hostname !== 'localhost'
) {
  window.location.replace(
    'https://www.stackform.ai' + window.location.pathname + window.location.search
  );
}
// ---------------------------------------------------------------------

/** Loading spinner shown while auth state is being determined. */
function AuthLoading() {
  return (
    <div className="login-wrapper">
      <div className="login-card" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <div className="spinner"></div>
        <div style={{ marginTop: '1rem', color: '#b5b9c9' }}>Signing in...</div>
      </div>
    </div>
  );
}

/** Redirect / to /chat or /login based on auth state. */
function RootRedirect() {
  const { user, authLoading } = useAuth();
  if (authLoading) return <AuthLoading />;
  return <Navigate to={user ? "/chat" : "/login"} replace />;
}

/** /login is only reachable when not authenticated.
 *  Reads the intended destination from location state so login flows
 *  (email + OAuth) can redirect back to the originally requested path. */
function LoginRoute() {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return <AuthLoading />;
  if (user) return <Navigate to="/chat" replace />;
  const redirectTo = location.state?.redirectTo || '/chat';
  return <LoginPage redirectTo={redirectTo} />;
}

/** Route guard — redirects unauthenticated users to /login, preserving
 *  the requested URL in location state so the login flow can return to it. */
function RequireAuth({ children }) {
  const { user, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return <AuthLoading />;
  if (user) return children;
  return (
    <Navigate
      to="/login"
      state={{ redirectTo: location.pathname + location.search }}
      replace
    />
  );
}

/** /register is only valid after an OAuth flow that produced a provisionalUser.
 *  If there is no provisional user (degenerate path), show LoginPage and thread
 *  the redirectTo from existing location state through so it isn't lost. */
function RegisterRoute() {
  const { user, provisionalUser, authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return <AuthLoading />;
  if (user) return <Navigate to="/chat" replace />;
  if (provisionalUser) return <RegistrationPage />;
  // No provisional user — someone navigated to /register directly.
  const redirectTo = location.state?.redirectTo || '/chat';
  return <LoginPage redirectTo={redirectTo} />;
}

/** Root app — wraps all routes. */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      <Route path="/register" element={<RegisterRoute />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route
        path="/chat"
        element={<RequireAuth><App /></RequireAuth>}
      />
      <Route
        path="/workflow-builder"
        element={<RequireAuth><WorkflowBuilder /></RequireAuth>}
      />
      <Route path="/" element={<RootRedirect />} />
      {/* Fallback — redirect unknown paths to root, which handles auth branching */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);

reportWebVitals();
